// import TextField from '@components/text-field/text-field';
import { Box, Chip, Grid, TextField, Typography } from "@mzc-pdc/ui";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { getUsersAPI } from "@modules/apis/user-management";
import { useSelector } from "react-redux";
import { AutocompleteUsers } from "@components_v2/autocomplete";
import { useEffect } from "react";
import { StyledAutocompletePopper } from "../chip-popper-components.styled";
import { useTranslation } from "react-i18next";
import { ImageEmptySearchMini, ImageSearch } from "@mzc-cloudplex/icons";
import theme from "@styles/theme";

export const ChipUsersInput = ({
    user,
    disabled = false,
    onDeleteUser,
    onAddUser,
    placeholder = undefined,
    isEdit,
}) => {
    const { t } = useTranslation();
    const space = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);
    const [open, setOpen] = useState(false);

    const [checkSearch, setCheckSearch] = useState(false); // Note : noOptionsText에서 검색 여부에 따라 내용이 달라져서 검색을 했는지 체크하기 위해 추가했습니다.
    const [value, setValue] = useState(user);
    const [searchedValue, setSearchedValue] = useState(""); //현재 검색중인 이름
    const [userOptions, setUserOptions] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null); //현재 선택한 유저(유저의 아이디만 popover로 넘겨주면 되서 자체력으로 autocomplete를 사용하도록 만들었습니다.)
    const [pendingUser, setPendingUser] = useState(false);
    const userPage = useRef(0);
    const totalUser = useRef(-1);
    const USER_LIMIT = 40;

    const getUserList = useCallback(
        async (isInitValue = false) => {
            try {
                if (pendingUser || totalUser.current === userOptions?.length || !value) return;
                setPendingUser(true);
                const response = await getUsersAPI(space.id, space.endpoint, project.id, {
                    name: value,
                    shouldRequestDetail: false,
                    offset: userPage.current,
                    limit: USER_LIMIT,
                });
                // console.log(userOptions);
                if (isInitValue) {
                    setUserOptions([...response.data.results]);
                    setCheckSearch(true);
                } else setUserOptions([...userOptions, ...response.data.results]);
                userPage.current += 1;
                totalUser.current = response.data.totalCount;
            } catch (error) {
                console.error(error);
            } finally {
                setPendingUser(false);
            }
        },
        [pendingUser, space, value, searchedValue, userOptions, project],
    );

    const onHandleDeleteUser = useCallback(
        (index) => {
            setSelectedUser(null);
            onDeleteUser();
            setSearchedValue("");
            setCheckSearch(false);
        },
        [onDeleteUser],
    );

    const onHandleSubmitUser = useCallback(() => {
        if (value !== searchedValue) {
            setSearchedValue(value);

            userPage.current = 0;
            totalUser.current = -1;
            getUserList(true);
        }
    }, [value, searchedValue, userOptions, onAddUser, getUserList]);

    const onHandleScrollUser = useCallback(() => {
        getUserList();
    }, [searchedValue, value, onAddUser]);

    const onHandleClickOption = useCallback(
        (selected) => {
            if (selected) {
                const foundUser = userOptions.find((user) => user.id === selected.id);
                setSelectedUser({ ...foundUser });
                setValue(foundUser);
                onAddUser({ id: foundUser?.id, name: foundUser?.name });
                setUserOptions([]);
                setCheckSearch(false);
            } else {
                onDeleteUser();
            }
        },
        [userOptions],
    );

    const handleInputChange = (reason) => {
        if (reason === "clear") {
            setSelectedUser(null);
            setValue(null);
        }
    };

    function PopperComponent(props) {
        const { disablePortal, anchorEl, open, ...other } = props;
        return <StyledAutocompletePopper {...other}></StyledAutocompletePopper>;
    }

    useEffect(() => {
        if (!user) getUserList(true); //최초 조회
        if (user) setValue(user);
    }, [user]);

    return (
        <AutocompleteUsers
            value={value?.id ? value : null}
            open={open}
            setOpen={setOpen}
            disabled={disabled}
            setValue={setValue}
            isEdit={isEdit}
            placeholder={placeholder}
            options={
                !pendingUser ? userOptions.map((user) => ({ name: user.name, id: user.id, email: user.email })) : []
            }
            checkDuplicateValue={false}
            inputDisabled={selectedUser}
            onInputChange={handleInputChange}
            onDelete={onHandleDeleteUser}
            onSubmit={onHandleSubmitUser}
            // onScroll={onHandleScrollUser} //TODO: 무한스크롤로 구현
            onClickOption={onHandleClickOption}
            popperComponent={PopperComponent}
            noOptionsText={
                !selectedUser && (
                    <Grid
                        container
                        justifyContent={"center"}
                        alignItems={`center`}
                        flexDirection={"column"}
                        sx={{ height: "97px" }}
                    >
                        {checkSearch ? (
                            <>
                                <ImageEmptySearchMini width={24} height={24} />
                                <Typography component={"span"} color={theme.palette.secondary[800]}>
                                    {t(`common::msg::No search results found.`)}
                                </Typography>
                            </>
                        ) : (
                            <>
                                <ImageSearch width={24} height={24} />
                                <Typography component={"span"} color={theme.palette.secondary[800]}>
                                    {t(`common::msg::Search for the owner of the asset.`)}
                                </Typography>
                            </>
                        )}
                    </Grid>
                )
            }
        ></AutocompleteUsers>
    );
};
